import {
  ParamListBase,
  RouteProp,
  useNavigation,
  useRoute,
} from '@react-navigation/core';
import NavigationRouteContext from '@react-navigation/core/src/NavigationRouteContext';
import { StackNavigationProp } from '@react-navigation/stack';
import { createContext, memo, ReactNode, useContext, useMemo } from 'react';
import type { AppModalParams } from '../../flows/Routes';

export type DefaultPopNavigatorParams = AppModalParams;

type PopNavigatorContextValue = null | {
  navigation: StackNavigationProp<DefaultPopNavigatorParams>;
  route: RouteProp<DefaultPopNavigatorParams>;
  parentContextValue: PopNavigatorContextValue;
};

const PopNavigatorContext = createContext<PopNavigatorContextValue>(null);

export const PopNavigatorProvider = memo((props: { children: ReactNode }) => {
  const parentCtxValue = useContext(PopNavigatorContext);

  const navigation =
    useNavigation<StackNavigationProp<DefaultPopNavigatorParams>>();

  // todo(@zhigang1992): we can't use useRoute here because it
  // will throw error when showcase view is rendered by seller view
  // we can remove this after we have migrated to the seller RN
  // const route = useRoute<RouteProp<AppModalParams>>();
  const route = useContext(
    NavigationRouteContext,
  ) as RouteProp<DefaultPopNavigatorParams>;

  const contextValue: PopNavigatorContextValue = useMemo(
    () => ({ navigation, route, parentContextValue: parentCtxValue }),
    [navigation, route, parentCtxValue],
  );

  return (
    <PopNavigatorContext.Provider value={contextValue}>
      {props.children}
    </PopNavigatorContext.Provider>
  );
});

export const useRootNavigation = <
  T extends ParamListBase = DefaultPopNavigatorParams,
>(): StackNavigationProp<T> => {
  let parentCtx = useContext(PopNavigatorContext);
  const navigation = useNavigation<StackNavigationProp<T>>();
  while (parentCtx?.parentContextValue) {
    parentCtx = parentCtx.parentContextValue;
  }
  const parentNav = parentCtx?.navigation as StackNavigationProp<T>;
  return parentNav ?? navigation;
};

// note(@zhigang1992): in cases like showcase navigator
// we have a nested navigator that id independent of the
// app navigator, but we need app navigator in cases like
// presenting login, or switching to a different show
export const usePopNavigation = <
  T extends ParamListBase = DefaultPopNavigatorParams,
>(): StackNavigationProp<T> => {
  const parentLevelNav = useContext(PopNavigatorContext)
    ?.navigation as StackNavigationProp<T>;
  const currLevelNav = useNavigation<StackNavigationProp<T>>();
  // if there are no parent level navigation, it means we are in top level
  // we can just use the top level navigation
  return parentLevelNav ?? currLevelNav;
};

export const usePopRoute = (): RouteProp<DefaultPopNavigatorParams> => {
  const rootRoute = useContext(PopNavigatorContext)?.route;
  const route = useRoute<RouteProp<DefaultPopNavigatorParams>>();
  return rootRoute ?? route;
};

export const useTopNavigation = <
  T extends ParamListBase = DefaultPopNavigatorParams,
>(): StackNavigationProp<T> => {
  const navigation = useNavigation<StackNavigationProp<T>>();
  return useMemo(() => {
    let curr = navigation;
    while (
      curr != null &&
      !curr.getState().routeNames.includes('debugPanel' as any)
    ) {
      curr = curr.getParent();
    }
    return curr ?? navigation;
  }, [navigation]);
};
